import React, { useEffect, useState } from 'react';
import { Box, Button, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import { fieldLabelSx, nativeInputSx } from '../common/formFieldStyles';

const emptySupplier = () => ({
  name: '',
  taxId: '',
  phone: '',
  email: '',
  notes: '',
});

const SupplierEditorDialog = ({ open, supplier, saving = false, onClose, onSave }) => {
  const { t } = useTranslation();
  const [form, setForm] = useState(emptySupplier);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(supplier ? {
      name: supplier.name || '',
      taxId: supplier.taxId || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      notes: supplier.notes || '',
    } : emptySupplier());
    setError('');
  }, [open, supplier]);

  const handleSubmit = () => {
    if (!form.name.trim()) {
      setError('stock.suppliers.validation.nameRequired');
      return;
    }

    onSave(form);
  };

  const field = (id, key, label, type = 'text') => (
    <Box>
      <Typography variant="subtitle2" sx={fieldLabelSx}>{label}</Typography>
      <Box
        component={key === 'notes' ? 'textarea' : 'input'}
        id={id}
        type={type}
        aria-label={label}
        value={form[key]}
        onChange={(event) => setForm((previous) => ({ ...previous, [key]: event.target.value }))}
        sx={nativeInputSx(key === 'name' && Boolean(error), key === 'notes')}
      />
    </Box>
  );

  return (
    <ScrollableDialog
      open={open}
      onClose={saving ? undefined : onClose}
      maxWidth="md"
      title={supplier ? t('stock.suppliers.editTitle') : t('stock.suppliers.createTitle')}
      dividers
      showCloseButton
      titleSx={{ pb: 1, flexShrink: 0 }}
      actionsSx={{ flexShrink: 0, justifyContent: 'space-between' }}
      actions={(
        <>
          <Button onClick={onClose} disabled={saving} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton id="supplier-save" onClick={handleSubmit} disabled={saving}>
            {saving ? t('common.saving') : t('common.save')}
          </PrimaryActionButton>
        </>
      )}
    >
      <Box id="supplier-editor-form" sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {field('supplier-name', 'name', `${t('stock.suppliers.name')} *`)}
        {error ? <Typography variant="caption" sx={{ color: 'error.main' }}>{t(error)}</Typography> : null}
        {field('supplier-tax-id', 'taxId', t('stock.suppliers.taxId'))}
        {field('supplier-phone', 'phone', t('stock.suppliers.phone'), 'tel')}
        {field('supplier-email', 'email', t('stock.suppliers.email'), 'email')}
        {field('supplier-notes', 'notes', t('stock.suppliers.notes'))}
      </Box>
    </ScrollableDialog>
  );
};

export default SupplierEditorDialog;
